import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

/**
 * DetailRequestContext - Global state management for detail request modals
 * 
 * Manages:
 * - Modal open/close state
 * - Content being processed
 * - Loading states
 * - API call results
 */
const DetailRequestContext = createContext(null);

export const useDetailRequest = () => {
  const context = useContext(DetailRequestContext);
  if (!context) {
    throw new Error('useDetailRequest must be used within a DetailRequestProvider');
  }
  return context;
};

export const DetailRequestProvider = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1); // 1 = confirmation, 2 = editing
  const [modalData, setModalData] = useState({
    title: '',
    originalContent: '',
    enhancedContent: '',
    editableContent: '', // For step 2 editing
    isLoading: false,
    error: null
  });
  const [applyCallback, setApplyCallback] = useState(null);

  /**
   * Open the modal with content to be enhanced (Step 1 - Confirmation)
   */
  const openModal = useCallback((title, originalContent, onApply = null) => {
    setModalData({
      title,
      originalContent,
      enhancedContent: '',
      editableContent: '',
      isLoading: false, // Start with no loading in step 1
      error: null
    });
    setApplyCallback(() => onApply); // Store as function reference
    setModalStep(1); // Start with confirmation step
    setIsModalOpen(true);
  }, []);

  /**
   * Close the modal and reset state
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    // Reset data after animation completes
    setTimeout(() => {
      setModalData({
        title: '',
        originalContent: '',
        enhancedContent: '',
        editableContent: '',
        isLoading: false,
        error: null
      });
      setApplyCallback(null);
      setModalStep(1);
    }, 300);
  }, []);

  /**
   * Update modal with enhanced content from API and move to step 2
   */
  const setEnhancedContent = useCallback((content) => {
    setModalData(prev => ({
      ...prev,
      enhancedContent: content,
      editableContent: content, // Initialize editable content with enhanced content
      isLoading: false,
      error: null
    }));
    setModalStep(2); // Move to editing step
  }, []);

  /**
   * Update modal with error state
   */
  const setError = useCallback((error) => {
    setModalData(prev => ({
      ...prev,
      isLoading: false,
      error: error.message || 'An error occurred'
    }));
  }, []);

  /**
   * Set loading state
   */
  const setLoading = useCallback((loading) => {
    setModalData(prev => ({
      ...prev,
      isLoading: loading
    }));
  }, []);

  /**
   * Proceed from step 1 to step 2 (start API call)
   */
  const proceedToEnhancement = useCallback(() => {
    setModalData(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));
    // The API call will be triggered by the button node when it receives this event
    const proceedEvent = new CustomEvent('proceedDetailRequestModal', {
      detail: { confirmed: true }
    });
    window.dispatchEvent(proceedEvent);
  }, []);

  /**
   * Update editable content in step 2
   */
  const updateEditableContent = useCallback((content) => {
    setModalData(prev => ({
      ...prev,
      editableContent: content
    }));
  }, []);

  /**
   * Apply enhanced content using stored callback (from step 2)
   */
  const applyEnhancedContent = useCallback(() => {
    if (applyCallback && modalData.editableContent) {
      applyCallback(modalData.editableContent);
      closeModal();
    }
  }, [applyCallback, modalData.editableContent, closeModal]);

  // Event listeners for communication with DetailRequestButtonNode
  useEffect(() => {
    const handleOpenModal = (event) => {
      const { title, originalContent, onApply } = event.detail;
      openModal(title, originalContent, onApply);
    };

    const handleUpdateModal = (event) => {
      const { enhancedContent, onApply } = event.detail;
      setEnhancedContent(enhancedContent);
      // Update the callback if provided
      if (onApply) {
        setApplyCallback(() => onApply);
      }
    };

    const handleErrorModal = (event) => {
      const { error } = event.detail;
      setError(error);
    };

    window.addEventListener('openDetailRequestModal', handleOpenModal);
    window.addEventListener('updateDetailRequestModal', handleUpdateModal);
    window.addEventListener('errorDetailRequestModal', handleErrorModal);

    return () => {
      window.removeEventListener('openDetailRequestModal', handleOpenModal);
      window.removeEventListener('updateDetailRequestModal', handleUpdateModal);
      window.removeEventListener('errorDetailRequestModal', handleErrorModal);
    };
  }, [openModal, setEnhancedContent, setError]);

  const contextValue = {
    // State
    isModalOpen,
    modalStep,
    modalData,
    
    // Actions
    openModal,
    closeModal,
    setEnhancedContent,
    setError,
    setLoading,
    proceedToEnhancement,
    updateEditableContent,
    applyEnhancedContent
  };

  return (
    <DetailRequestContext.Provider value={contextValue}>
      {children}
    </DetailRequestContext.Provider>
  );
};

export default DetailRequestContext;
